/**
 * `useActivitySnapshot` — renderer-side subscriber for the
 * `activity:state` push channel (umbrella design
 * `docs/agent-transparency-design.md`, C2 plan
 * `docs/c2-transparency-banner-design.md` §10).
 *
 * Returns the latest `ActivitySnapshot` for the given `gameId`, or
 * `null` when no build is in flight for this game. Subscribes on
 * mount (and on every `gameId` change), unsubscribes on unmount and
 * on `gameId` change.
 *
 * Wire shape is FROZEN — see `ActivitySnapshot` / `ActivityStatePush`
 * / `AgentActivity` in `../../shared/ipc-channels.ts`. A
 * `sessionCreatedAt` wire extension is filed as Crucible #679 and is
 * deferred to post-C2; this hook orders opaque session strings by
 * the timestamp at which their first push was observed locally.
 *
 * Discard rules (Codex round-1 HIGH on the original design draft —
 * blindly accepting any push with a different `sessionId` is unsafe
 * because a stale session's late tail-push could clobber a newer
 * session's banner; blindly accepting `snapshot: null` is unsafe for
 * the same reason):
 *
 *   1. Cross-game pushes (`push.gameId !== gameId`) are dropped
 *      silently — the same `crucible` instance can have multiple
 *      games' registries active in main and the renderer must filter
 *      defensively even though the route currently mounts one game
 *      at a time.
 *   2. `snapshot: null` for a session that has not yet been the
 *      active session is a no-op (idempotent) — there is nothing to
 *      clear. Once there is an active session, the next null clears
 *      it.
 *   3. Same-session pushes follow `(activeTurnId, seq)` tuple
 *      ordering: a strictly older `activeTurnId` is dropped; same
 *      `activeTurnId` with `seq <=` latest is dropped.
 *   4. Cross-session pushes are accepted only when the incoming
 *      session was first observed AFTER the currently-active
 *      session. The per-`sessionId` first-seen `Map` is the only
 *      ordering signal available without a wire change (see #679).
 *
 * Workspace-side reinforcement (belt-and-braces): the centre-column
 * shell that hosts this hook mounts with `key={gameId}` so the
 * effect remount on game navigation drops any in-flight state. The
 * `useEffect` cleanup below is the load-bearing path; the key reset
 * is the second seatbelt.
 */

import { useEffect, useRef, useState } from 'react';
import type {
  ActivitySnapshot,
  ActivityStatePush,
} from '@volley/crucible-ipc-types/ipc-channels';

export function useActivitySnapshot(
  gameId: string,
): ActivitySnapshot | null {
  const [snapshot, setSnapshot] = useState<ActivitySnapshot | null>(null);
  const activeSessionId = useRef<string | null>(null);
  const latestTuple = useRef<{
    sessionId: string;
    activeTurnId: number;
    seq: number;
  } | null>(null);
  const firstSeenAtForSession = useRef<Map<string, number>>(new Map());

  useEffect(() => {
    // Reset all refs + state for the new gameId. Belt-and-braces with
    // the Workspace-side `key={gameId}` remount, but load-bearing on
    // its own — if a caller forgets the key, the cleanup still drops
    // every cross-game leak.
    activeSessionId.current = null;
    latestTuple.current = null;
    firstSeenAtForSession.current = new Map();
    setSnapshot(null);

    const unsubscribe = window.crucible.on(
      'activity:state',
      (push: ActivityStatePush) => {
        // Cross-game guard — drop silently. The same renderer can
        // observe pushes for any game whose registry is alive in
        // main; the hook MUST filter on the local `gameId` closure.
        if (push.gameId !== gameId) return;

        const next = push.snapshot;

        // Null-snapshot rule. A null carries no tuple, only a
        // gameId. Treat it as terminal-for-current-session ONLY when
        // there is an active session to clear; otherwise it is a
        // no-op (idempotent). Stale tails of a previous session
        // would otherwise unmount a valid banner.
        if (next === null) {
          if (activeSessionId.current !== null) {
            activeSessionId.current = null;
            latestTuple.current = null;
            setSnapshot(null);
          }
          return;
        }

        // First push observed for this sessionId — record its
        // first-seen timestamp. The guard MUST run before the
        // cross-session comparison below so the comparison can read
        // a `firstSeenAt` for the incoming session.
        if (!firstSeenAtForSession.current.has(next.sessionId)) {
          firstSeenAtForSession.current.set(next.sessionId, Date.now());
        }

        // Cross-session discard. If we already have an active
        // session AND the incoming push is for a different session,
        // accept it only when the incoming session was first
        // observed AFTER the active session. This drops a stale
        // session's late tail-push that arrives after the
        // user-driven navigation has already produced the new
        // session's first push.
        if (
          activeSessionId.current !== null &&
          activeSessionId.current !== next.sessionId
        ) {
          // `firstSeenAtForSession.current.get(next.sessionId)` is
          // guaranteed to exist because the guard above set it on
          // the same tick. We assert with `!` to keep the type
          // narrow without an `any` cast.
          const incomingFirstSeen = firstSeenAtForSession.current.get(
            next.sessionId,
          );
          const activeFirstSeen = firstSeenAtForSession.current.get(
            activeSessionId.current,
          );
          if (incomingFirstSeen === undefined) {
            // Defensive — should be unreachable because the guard
            // above just set it. Treat as "drop" rather than
            // silently roll forward.
            return;
          }
          if (
            activeFirstSeen !== undefined &&
            incomingFirstSeen <= activeFirstSeen
          ) {
            // The "different" session is actually older. Drop.
            return;
          }
          // The incoming session is newer. Roll forward — clear the
          // tuple cache so the new session's first push is accepted
          // by the tuple check below.
          activeSessionId.current = next.sessionId;
          latestTuple.current = null;
        }

        // First push for this hook's lifetime — adopt this sessionId
        // as active.
        if (activeSessionId.current === null) {
          activeSessionId.current = next.sessionId;
        }

        // Within-session tuple ordering. See umbrella design §2
        // "Push cadence". `seq` resets to 0 on every `activeTurnId`
        // advance, so the comparison is two-stage.
        const cur = latestTuple.current;
        if (cur && cur.sessionId === next.sessionId) {
          if (next.activeTurnId < cur.activeTurnId) return;
          if (
            next.activeTurnId === cur.activeTurnId &&
            next.seq <= cur.seq
          ) {
            return;
          }
        }
        latestTuple.current = {
          sessionId: next.sessionId,
          activeTurnId: next.activeTurnId,
          seq: next.seq,
        };
        setSnapshot(next);
      },
    );

    return unsubscribe;
  }, [gameId]);

  return snapshot;
}
